import { useLocation } from 'react-router-dom'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { cn } from '@/shared/lib/cn'
import { scrollMainToTop } from '@/shared/lib/scrollContainer'
import { GuardedNavLink } from '@/shared/ui/GuardedNavLink'
import { getNavigationItems } from '@/widgets/navigation/navItems'
import { useMobileMenuStore } from '@/widgets/navigation/mobileMenuStore'

interface BottomNavigationProps {
  hidden?: boolean
}

const normalizePath = (pathname: string) => {
  const trimmed = pathname.replace(/\/$/, '')
  return trimmed.length > 0 ? trimmed : '/'
}

const isSameNavTarget = (currentPath: string, targetPath: string) => {
  const current = normalizePath(currentPath)
  const target = normalizePath(targetPath)
  if (current === target) return true
  if (target === '/dashboard' && current === '/') return true
  return false
}

export const BottomNavigation = ({ hidden = false }: BottomNavigationProps) => {
  const location = useLocation()
  const role = useSessionStore((s) => s.user?.role ?? 'doctor')
  const items = getNavigationItems(role)
  const menuOpen = useMobileMenuStore((s) => s.open)
  const setMenuOpen = useMobileMenuStore((s) => s.setOpen)

  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-30 border-t border-[var(--color-border)] bg-[var(--color-chrome)] px-2 pb-[calc(env(safe-area-inset-bottom)+6px)] pt-2 md:hidden',
        hidden && 'hidden',
      )}
    >
      <ul
        className="grid items-end gap-1"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((item) => {
          const mobileLabel = item.mobileLabel ?? item.label

          if (item.kind === 'menu') {
            return (
              <li key={item.to} className="flex">
                <button
                  type="button"
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-expanded={menuOpen}
                  aria-haspopup="dialog"
                  className={cn(
                    'group flex min-h-11 w-full flex-col items-center justify-center gap-1 px-2 py-2 text-[10px] font-semibold tracking-wide transition-colors',
                    menuOpen ? 'text-[var(--color-nav-active)]' : 'text-[var(--color-chrome-foreground-muted)]',
                  )}
                >
                  <item.icon
                    size={19}
                    aria-hidden
                    className={cn(
                      'transition-colors',
                      menuOpen ? 'text-[var(--color-nav-active)]' : 'text-[var(--color-chrome-foreground-muted)]',
                    )}
                  />
                  <span>{mobileLabel}</span>
                </button>
              </li>
            )
          }

          return (
            <li key={item.to} className="flex">
              <GuardedNavLink
                to={item.to}
                onClick={() => {
                  if (isSameNavTarget(location.pathname, item.to)) {
                    scrollMainToTop({ behavior: 'smooth' })
                  }
                }}
                className={({ isActive }) =>
                  cn(
                    'group flex min-h-11 w-full flex-col items-center justify-center gap-1 px-2 py-2 text-[10px] font-semibold tracking-wide transition-colors',
                    isActive ? 'text-[var(--color-nav-active)]' : 'text-[var(--color-chrome-foreground-muted)]',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      size={19}
                      aria-hidden
                      className={cn(
                        'transition-colors',
                        isActive ? 'text-[var(--color-nav-active)]' : 'text-[var(--color-chrome-foreground-muted)]',
                      )}
                    />
                    <span>{mobileLabel}</span>
                  </>
                )}
              </GuardedNavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
